/**
 * Who one row of a crosstab is: a person, never a diary entry (M4S-5777, defect 10, X20).
 *
 * survey_portal writes one `survey_respondent` row per diary entry, keyed
 * (survey_id, panelist_id, diary_count). The pivot grouped by all three, so a diary or any other
 * multi-entry study counted each entry as a respondent: survey 151086 has 1,253 complete entries
 * from 880 people, and every base, percentage, wave n= and test read 1,253. Feasibility already
 * counts people, not rows (`src/lib/feasibility/refresh/derivations.ts`, "a diary writes a row
 * per entry"); the crosstab now does too.
 *
 * The rule (decision D3): a person counts once per survey. A person is in a cell when ANY of
 * their entries matches it, so a person's answers to a question are what each entry gave, read as
 * a set when counting. A numeric question gives each person one value, the mean of their entries'
 * answers (`personNumber`), and a range or a comparison holds when any entry's answer passes it
 * (`anyNumber`). A person in two surveys of a project (two waves, two phases) is still one row in each:
 * this never merges across surveys.
 *
 * This file is the one home of that rule. `pivotResponses` runs `collapseEntriesToPeople` on what
 * it reads, so every reader of the pivot (the workspace, the exports, the iOOH deck, Slide
 * Creator's /computed route, Reports and DANI's crosstab context) sees people. Pure.
 */

import { respondentRowId } from './respondent-id';

/** How multi-answer values are joined in a response cell (the pivot's STRING_AGG separator). */
export const ANSWER_SEPARATOR = ' AND ';

/** One complete diary entry, as the pivot query reads it. */
export interface EntryRow {
    panelistId: string;
    surveyId: string;
    /** `diary_count`: 0 on a study without entries. */
    entry: number;
    /** ISO time the entry was completed, or null. */
    respondedAt: string | null;
    /** Uppercased qid to the entry's answer, `' AND '`-joined. */
    values: Record<string, string | null> | null;
}

/** One person in one survey, after their entries are combined. */
export interface PersonRow {
    /** `respondentRowId(panelist, survey)`. */
    rowId: string;
    surveyId: string;
    /** The latest of their entries' completion times. */
    respondedAt: string | null;
    values: Record<string, string | null>;
}

/** Per survey: how many people the crosstab counts, and how many complete entries they made. */
export interface SurveyEntryCount {
    people: number;
    entries: number;
}

export type EntryProfile = Record<string, SurveyEntryCount>;

/**
 * Where one entry's answers end and the next one's begin, in a merged cell: an empty answer
 * between two separators ("3 AND  AND 8"). Every reader that splits a cell on `ANSWER_SEPARATOR`
 * already drops empty answers, so a counting reader sees the answers and nothing else, while
 * `cellEntries` can still tell a diary person's entries apart (M4S-5777 review).
 */
export const ENTRY_SEPARATOR = `${ANSWER_SEPARATOR}${ANSWER_SEPARATOR}`;

/**
 * Several entries' answers to one question, in entry order. A single value comes back unchanged
 * (byte for byte), so a study with one entry per person reads exactly as before.
 *
 * Each entry that answered keeps its own answers (already distinct: the pivot's
 * `STRING_AGG(DISTINCT ...)`), joined by `ENTRY_SEPARATOR`, so an answer given on two entries is
 * listed twice. Every reader that counts reads the cell as a set, so a person is in each answer
 * any entry gave (decision D3). Mean, Median and a formula variable read the entries, so a person
 * who answered 5, 5 and 7 has the one value 17/3 (`personNumber`).
 */
export function mergeEntryValues(values: Array<string | null | undefined>): string | null {
    const present = values.filter((v): v is string => typeof v === 'string' && v !== '');
    if (present.length === 0) return null;
    if (present.length === 1) return present[0];
    const entries: string[] = [];
    for (const value of present) {
        const tokens = [
            ...new Set(
                value
                    .split(ANSWER_SEPARATOR)
                    .map((part) => part.trim())
                    .filter(Boolean),
            ),
        ];
        if (tokens.length > 0) entries.push(tokens.join(ANSWER_SEPARATOR));
    }
    if (entries.length === 0) return null;
    return entries.join(ENTRY_SEPARATOR);
}

/**
 * A cell's answers grouped by entry: one group for a cell that is not a merged diary cell, one
 * per entry for a cell `mergeEntryValues` joined. Answers are trimmed, never case-changed.
 */
export function cellEntries(value: string): string[][] {
    const entries: string[][] = [[]];
    for (const part of value.split(ANSWER_SEPARATOR)) {
        const token = part.trim();
        if (token) entries[entries.length - 1].push(token);
        else if (entries[entries.length - 1].length > 0) entries.push([]);
    }
    return entries.filter((e) => e.length > 0);
}

function numberOf(token: string): number | null {
    const n = parseFloat(token);
    return Number.isNaN(n) ? null : n;
}

/**
 * The one number a diary person gives a numeric question (M4S-5777 review): the mean of every
 * numeric answer across their entries, repeats included, so each entry counts once toward it and
 * the person counts once. Null when no answer is a number. Mean, Median and formula variables
 * read a merged cell this way, so their n counts people.
 */
export function personNumber(
    entries: ReadonlyArray<ReadonlyArray<string>>,
    /** What an answer is worth; null leaves it out. A Mean passes its scale's rule (`meanRuleOf`,
     *  scale.ts: Don't know and N/A out); a formula reads the number as written. */
    valueOf: (token: string) => number | null = numberOf,
): number | null {
    let sum = 0;
    let count = 0;
    for (const entry of entries) {
        for (const token of entry) {
            const n = valueOf(token);
            if (n == null || Number.isNaN(n)) continue;
            sum += n;
            count += 1;
        }
    }
    return count > 0 ? sum / count : null;
}

/**
 * A diary person's answers grouped by entry, for a parsed cell (a Set of answers) that
 * `mergeEntryValues` joined from two or more entries. Keyed by the Set itself, so nothing else
 * carries it; a cell that is not a merged diary cell has no entry and is read exactly as before.
 * Compute marks the cells it parses (`markEntryCell`) and every reader asks `entryCellOf`, so the
 * "any entry" reading (D3) reaches only diary people and never an ordinary multi-answer cell.
 */
const entryCells = new WeakMap<ReadonlySet<string>, string[][]>();

/** Records that this parsed cell is a diary person's, with their answers by entry. */
export function markEntryCell(cell: ReadonlySet<string>, entries: string[][]): void {
    entryCells.set(cell, entries);
}

/** A parsed cell's answers by entry when it is a diary person's merged cell, else undefined. */
export function entryCellOf(cell: ReadonlySet<string> | undefined): string[][] | undefined {
    return cell ? entryCells.get(cell) : undefined;
}

/**
 * True when some numeric answer in the cell passes `test`. Range buckets and the order operators
 * (`<`, `<=`, `>`, `>=`) read a person this way: a person is in a range, or passes a comparison,
 * when any of their entries does (decision D3). Identical on a cell with one answer.
 */
export function anyNumber(tokens: Iterable<string>, test: (n: number) => boolean): boolean {
    for (const token of tokens) {
        const n = parseFloat(token);
        if (!Number.isNaN(n) && test(n)) return true;
    }
    return false;
}

/**
 * True when every respondent holds at most one answer to a question. After the collapse a
 * single-select question on a diary study can hold several answers per person, so a test that
 * assumes exclusive categories (chi-square, a row read as a partition) must check the data with
 * this, not the question type (docs/pdlc/crosstabs-respondents.md §5.7).
 */
export function isExclusivePerRespondent(
    answers: Iterable<ReadonlySet<string> | null | undefined>,
): boolean {
    for (const set of answers) if (set && set.size > 1) return false;
    return true;
}

/**
 * Combines each person's entries into one row per survey, keeping the order people first
 * appear in. A person with one entry keeps their values object as it was.
 */
export function collapseEntriesToPeople(rows: EntryRow[]): {
    people: PersonRow[];
    entryProfile: EntryProfile;
} {
    const groups = new Map<string, EntryRow[]>();
    for (const row of rows) {
        const key = respondentRowId(row.panelistId, row.surveyId);
        const group = groups.get(key);
        if (group) group.push(row);
        else groups.set(key, [row]);
    }

    const entryProfile: EntryProfile = {};
    const people: PersonRow[] = [];
    for (const [rowId, group] of groups) {
        const surveyId = group[0].surveyId;
        const count = (entryProfile[surveyId] ??= { people: 0, entries: 0 });
        count.people += 1;
        count.entries += group.length;

        if (group.length === 1) {
            const only = group[0];
            people.push({
                rowId,
                surveyId,
                respondedAt: only.respondedAt,
                values: only.values ?? {},
            });
            continue;
        }

        const ordered = [...group].sort((a, b) => a.entry - b.entry);
        const qids = new Set<string>();
        for (const e of ordered) for (const q of Object.keys(e.values ?? {})) qids.add(q);
        const values: Record<string, string | null> = {};
        for (const qid of qids) {
            const merged = mergeEntryValues(ordered.map((e) => e.values?.[qid]));
            if (merged != null) values[qid] = merged;
        }
        const respondedAt = ordered.reduce<string | null>(
            (latest, e) =>
                e.respondedAt != null && (latest == null || e.respondedAt > latest)
                    ? e.respondedAt
                    : latest,
            null,
        );
        people.push({ rowId, surveyId, respondedAt, values });
    }
    return { people, entryProfile };
}

/** People and entries over the given surveys (every survey in the profile when none are given). */
export function entryTotals(
    profile: EntryProfile | null | undefined,
    surveyIds?: string[] | null,
): SurveyEntryCount {
    const totals = { people: 0, entries: 0 };
    if (!profile) return totals;
    const ids = surveyIds?.length ? surveyIds : Object.keys(profile);
    for (const sid of ids) {
        const c = profile[sid];
        if (!c) continue;
        totals.people += c.people;
        totals.entries += c.entries;
    }
    return totals;
}

/** True when some survey in scope has a person with more than one complete entry. */
export function isMultiEntry(
    profile: EntryProfile | null | undefined,
    surveyIds?: string[] | null,
): boolean {
    const t = entryTotals(profile, surveyIds);
    return t.entries > t.people;
}

const fmt = (n: number) => n.toLocaleString('en-US');

/**
 * The line every base states on a multi-entry study (X20: "a diary or multi-entry study says
 * whether it counts entries or people"). Null on a study with one entry per person, so nothing
 * changes there. Used by the workspace legend, every export and DANI's crosstab context.
 *
 * Its people and entries are the loaded surveys' (the study), never a filtered base's. A filter or
 * "all phases" narrows the n printed beside it, so it says "in the loaded surveys" and is never
 * worded as that n's breakdown.
 */
export function respondentUnitNote(
    profile: EntryProfile | null | undefined,
    surveyIds?: string[] | null,
): string | null {
    if (!isMultiEntry(profile, surveyIds)) return null;
    const t = entryTotals(profile, surveyIds);
    return (
        `Counts people, not diary entries. In the loaded surveys, ${fmt(t.people)} people made ` +
        `${fmt(t.entries)} complete entries. A person counts once per survey in each base and ` +
        `is in a cell when any of their entries matches it.`
    );
}

/**
 * The noun for an n= on a multi-entry study ("n=410 people"), or null (the caller keeps its own,
 * "respondents" or nothing). It names the unit only, so it is right beside a filtered n.
 */
export function respondentUnitNoun(
    profile: EntryProfile | null | undefined,
    surveyIds?: string[] | null,
): 'people' | null {
    return isMultiEntry(profile, surveyIds) ? 'people' : null;
}

/**
 * The study context for a one-line header, a clause that stands on its own
 * ("study: 880 people made 1,253 entries"), or null. Never spliced in as the noun after an n=:
 * that n may be filtered and these figures are not.
 */
export function respondentUnitShort(
    profile: EntryProfile | null | undefined,
    surveyIds?: string[] | null,
): string | null {
    if (!isMultiEntry(profile, surveyIds)) return null;
    const t = entryTotals(profile, surveyIds);
    return `study: ${fmt(t.people)} people made ${fmt(t.entries)} entries`;
}
